import uniqBy from 'lodash/uniqBy'

import { createSelector } from '@app/utils/createSelector'
import { catchFetchDataError } from '@app/utils/performFetchData'

import { postTelegramCode } from '@app/packages/api/v2/actions/telegram'
import { getTelegramBotConnections } from '@app/packages/api/v2/actions/telegram_bot_connections'
import type { ApiTelegramBotConnection } from '@app/packages/api/v2/types/entities'
import { createPromise } from '@app/packages/thunkSelector/promiseState'
import { createLoadable, mapThunkSelector } from '@app/packages/thunkSelector/thunkSelector'

import { callV2Api } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { configSelector } from '@app/store/slices/config'

import { profileUserResultSelector } from './profile'

const telegramCodeSlice = createReduxSlice<{ hash: string; code: string }>('telegram_code')
const telegramBotConnectionsSlice = createReduxSlice<{ user_id: string; list: ApiTelegramBotConnection[] }>('telegram_bot_connections')

export const addTelegramBotConnectionSlice = (connection: ApiTelegramBotConnection) =>
  telegramBotConnectionsSlice.update(state => (state ? { ...state, list: uniqBy([connection, ...state.list], t => t.id) } : state))

export const removeTelegramBotConnectionSlice = (id: string) =>
  telegramBotConnectionsSlice.update(state => (state ? { ...state, list: state.list.filter(t => t.id !== id) } : state))

export const telegramCodeResultSelector = createSelector([telegramCodeSlice.selector, profileUserResultSelector], (meta, profileResult) =>
  createLoadable(c => {
    const profile = c.unwrap(profileResult)
    if (!profile) throw new Error('Profile is missing')
    const hash = profile?.id
    if (!meta || meta.hash !== hash) {
      return c.load(
        createPromise('Telegram code must be fetched', hash, async dispatch => {
          const resp = await dispatch(callV2Api(postTelegramCode())).catch(catchFetchDataError(dispatch, 'Telegram code fetch failed'))
          const code = resp.data.id
          dispatch(telegramCodeSlice.set({ hash, code }))
        })
      )
    }
    return meta.code
  })
)

export const telegramBotConnectionsResultSelector = createSelector([telegramBotConnectionsSlice.selector, profileUserResultSelector], (meta, userResult) =>
  createLoadable(c => {
    const user = c.unwrap(userResult)
    if (!user || user.account_type === 'visitor') return null
    if (!meta || user.id !== meta.user_id)
      return c.load(
        createPromise('Telegram bot connections must be fetched', user.id, async dispatch => {
          const resp = await dispatch(callV2Api(getTelegramBotConnections())).catch(catchFetchDataError(dispatch, 'Telegram connections fetch failed'))
          dispatch(telegramBotConnectionsSlice.set({ user_id: user.id, list: resp.data }))
        })
      )
    return meta.list
  })
)

export const telegramBotConnectedResultSelector = mapThunkSelector(telegramBotConnectionsResultSelector, connections => {
  if (!connections) return true
  return connections.length > 0
})

export const telegramBotTokenSelector = createSelector([configSelector], config => config.telegramBotToken)
export const telegramBotCredentialsSelector = createSelector([telegramBotTokenSelector], token => {
  if (!token) return null
  const [name, id] = token.split(':')
  return { name, id }
})
